<template>
  <div class="__root__">
    <div class="-links">
      <ul class="wt2-inline-list">
        <li v-if="!removeFeatures.includes('link_info')">
          <a
            href="#"
            @click.prevent="$emit('action', { type: 'info', storyboard })"
          >
            <i class="wticon wticon-pencil3" />
            <span>{{ 'instantVideos.editInformation' | culture }}</span>
          </a>
        </li>
        <li>
          <a href="#" @click.prevent="copyLink">
            <input type="text" readonly ref="inputCopyUrl" :value="copyUrl" />
            <i class="wticon wticon-link2" />
            <span v-if="isCopied" class="wt2-color-disabled">{{
              'instantVideos.linkCopied' | culture
            }}</span>
            <span v-else>{{ 'instantVideos.copyLink' | culture }}</span>
          </a>
        </li>
        <li v-if="!removeFeatures.includes('link_share_on_slack')">
          <a
            href="#"
            @click.prevent="$emit('action', { type: 'slack', storyboard })"
          >
            <i class="wticon wticon-slack-small-icon" />
            {{ 'instantVideos.shareOnSlack' | culture }}
          </a>
        </li>
        <li v-if="!removeFeatures.includes('link_makeACopy')">
          <a
            href="#"
            @click.prevent="$emit('action', { type: 'makeACopy', storyboard })"
          >
            <i class="wticon wticon-copy" />
            {{ 'generic.MAKE_COPY' | culture }}
          </a>
        </li>
        <li v-if="!removeFeatures.includes('link_delete')">
          <a
            class="wt2-color-link"
            href="#"
            @click.prevent="$emit('action', { type: 'delete', storyboard })"
          >
            <i class="wticon wticon-trash2" />
            {{ 'instantVideos.delete' | culture }}
          </a>
        </li>
      </ul>
    </div>
    <div class="-body">
      <div class="wt2-text-l -title">{{ storyboard.title }}</div>
      <div class="wt2-text-n -info">
        <span class="-label">{{ 'storyboards.brand' | culture }}:</span>
        {{ storyBrandName }}
      </div>
      <div
        class="wt2-text-n -info"
        v-if="storyboard.categories && storyboard.categories.length"
      >
        <div class="-category-list">
          <span class="-label"
            >{{ 'dashboardFilter.categories' | culture }}:</span
          >
          <template v-for="(category, i) in storyboard.categories">
            <div
              v-if="i > 0"
              :key="`p-${i}-${category.id}`"
              class="-punctuation"
              v-text="','"
            />
            <div
              :key="`n-${i}-${category.id}`"
              class="-category-name"
              v-text="category.name"
            />
          </template>
        </div>
      </div>
      <div class="wt2-text-n -info">
        <span class="-label">ID:</span>
        {{ storyboard.id }}
      </div>
      <div v-if="storyboard.description" class="wt2-text-n -description">
        {{ storyboard.description }} &nbsp;
      </div>
      <div v-if="!hidePublishOptions" class="wt2-text-n -info -publish">
        <div class="-checkbox-group">
          <span class="-label">{{ 'storyboards.publish' | culture }}:</span>
          <wt-check-box
            v-if="!removeFeatures.includes('publish_to_shortcut')"
            v-tooltip="shortcutTooltip"
            @changed="
              $emit('action', {
                type: 'publish',
                storyboard,
                data: {
                  shortcut: $event,
                },
              })
            "
            :checked="storyboard.shortcutEnabled"
            :label-right="'storyboards.shortcut' | culture"
            :disabled="storyboardHasAudio"
          />
          <wt-check-box
            v-if="!removeFeatures.includes('publish_to_mass_produce')"
            v-tooltip="massProduceTooltip"
            @changed="
              $emit('action', {
                type: 'publish',
                storyboard,
                data: {
                  massProduce: $event,
                },
              })
            "
            :checked="storyboard.massProduceEnabled"
            :label-right="'storyboards.massProduce' | culture"
          />
        </div>
      </div>
    </div>
    <div class="-actions">
      <wt-button
        v-if="!removeFeatures.includes('btn_add2MyTemplates')"
        :text="'storyboards.add2MyTemplates' | culture"
        :processing="isDuplicating"
        @click.native="
          $emit('action', { type: 'btn_add2MyTemplates', storyboard })
        "
      />
      <wt-button
        v-if="!removeFeatures.includes('btn_useThisStoryBoard')"
        :text="'generic.USE_THIS_STORY_TEMPLATE' | culture"
        @click.native="
          $emit('action', { type: 'useThisStoryBoard', storyboard })
        "
      />
      <wt-button
        v-if="!removeFeatures.includes('btn_makeACopy')"
        :text="
          (myTemplatesTab
            ? 'generic.MAKE_COPY'
            : 'storyboards.copy2MyTemplates') | culture
        "
        :type="myTemplatesTab ? 'primary' : 'hollow-dark'"
        @click.native="$emit('action', { type: 'makeACopy', storyboard })"
      />
      <wt-button
        v-if="!removeFeatures.includes('btn_edit')"
        :text="'generic.EDIT_STORYBOARD' | culture"
        type="hollow-dark"
        @click.native="$emit('action', { type: 'edit', storyboard })"
      />
      <wt-button
        v-if="!removeFeatures.includes('mass_produce')"
        :text="'generic.MASS_PRODUCE' | culture"
        type="secondary"
        @click.native="$emit('action', { type: 'massProduce', storyboard })"
      />
    </div>
  </div>
</template>

<script>
import app from 'app';
import Vue from 'vue';
import { mapState } from 'vuex';
import { STORYBOARD_UI_FEATURES } from '#/const.ts';

export default Vue.extend({
  props: {
    storyboard: { type: Object, required: true },
    videoUrlMyTeam: { type: String, required: false, default: '' },
    removeFeatures: {
      type: Array,
      default: () => [],
      validator: (keys) => {
        if (keys.length === 0) {
          return true;
        } else {
          const supported = keys.filter((key) =>
            STORYBOARD_UI_FEATURES.includes(key)
          );
          return supported.length === keys.length;
        }
      },
    },
    isDuplicating: { type: Boolean, default: false },
    myTemplatesTab: { type: Boolean, required: true },
  },

  data() {
    return {
      isCopied: false,
    };
  },

  computed: {
    ...mapState('user', ['organizations']),

    copyUrl() {
      return app.generatePublishUrl(this.storyboard.id);
    },

    publishCommonTooltip() {
      return {
        animation: false,
        trigger: 'hover',
        container: '.__root__',
        placement: 'top',
        delay: { show: 250, hide: 0 },
      };
    },

    storyboardHasAudio() {
      return this.storyboard.type === 'VO';
    },

    shortcutTooltip() {
      const title = this.storyboardHasAudio
        ? app.culture.get('storyboards.shortcutWithoutAudio')
        : app.culture.get('storyboards.shortcutExplain');
      return {
        ...this.publishCommonTooltip,
        title,
      };
    },

    massProduceTooltip() {
      return {
        ...this.publishCommonTooltip,
        title: app.culture.get('storyboards.massProduceExplain'),
      };
    },

    hidePublishOptions() {
      return this.removeFeatures.includes('publish');
    },

    storyBrandName() {
      let rv = this.storyboard.programmerName;

      for (const org of this.organizations) {
        const brand = org.brands.find(
          (brand) =>
            brand.brandId === this.storyboard.brandId &&
            brand.programmerId === this.storyboard.programmerId
        );
        if (brand) {
          rv = brand.name;
          break;
        }
      }

      return rv;
    },
  },

  methods: {
    copyLink() {
      const input = this.$refs.inputCopyUrl;
      if (input && input.select && !this.isCopied) {
        input.select();
        input.blur();

        try {
          // copy text
          document.execCommand('copy');
          this.isCopied = true;

          _.delay(() => {
            this.isCopied = false;
          }, app.TIME.SECOND * 3);
        } catch (err) {
          console.warn(
            `fullPreview::copyLink->fail err: ${JSON.stringify(err)}`
          );
        }
      }
    },
  },
});
</script>
<style lang="scss" scoped>
@import 'style_guide/_sg_variables.scss';
.__root__ {
  /deep/ .tooltip-arrow {
    border-top-color: #fff !important;
  }
  display: flex;
  flex-direction: column;
  height: 100%;
  ul.wt2-inline-list {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    li {
      padding-right: 20px;
      padding-bottom: 5px;
      flex: 0 1 auto;
      display: block;
      &::after {
        content: '';
        padding: 0;
      }
    }
  }
  .-links {
    margin: 0;
    a:hover {
      color: darken($color-primary, 15%);
    }
  }
  .-title {
    color: rgba(0, 0, 0, 0.75);
    font-size: 18px;
    font-weight: 500;
    line-height: 20px;
    margin: 15px 0 18px;
  }
  .-label {
    color: rgba(0, 0, 0, 0.75);
    font-size: 14px;
    font-weight: 500;
    margin-right: 15px;
  }
  .-body {
    display: flex;
    flex-direction: column;
    flex: auto;
    .-info {
      margin-bottom: 5px;
      &.-publish {
        margin-top: auto;
        margin-bottom: 20px;
        .-label {
          margin-right: 30px;
          line-height: 14px;
          margin-top: 4px;
        }
      }
    }
  }
  .-description {
    height: auto;
    max-height: 3em;
    margin: 9px 0;
  }
  .-checkbox-group {
    display: flex;
    justify-content: left;
    /deep/ {
      .-label-right {
        max-width: 100%;
        margin-left: 0;
        margin-right: 30px;
      }
      .-body {
        margin-right: 6px;
      }
    }
  }
  .-actions {
    margin-top: auto;
    button {
      margin-bottom: 0;
    }
  }

  .-category-list {
    display: inline-flex;
    flex-wrap: wrap;

    .-category-name {
      @include single_line_ellipsis();
      max-width: 160px;
    }
    .-punctuation {
      margin-right: 4px;
    }
  }
}
</style>
